import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  Clock3,
  Download,
  Eye,
  FileText,
  Filter,
  History,
  RefreshCw,
  Search,
  ServerCog,
  ShieldCheck,
  UserRoundCheck,
  X,
} from "lucide-react";
import { api } from "../../services/api";
import { PaginationControls, StatusPill } from "./AdminPanelParts";
import { useAdminResource } from "./useAdminResource";
import { usePagination } from "./usePagination";
import { ToastStack, useToast } from "./useToast";

const fallbackPayload = {
  logs: [
    {
      _id: "preview-1",
      actorEmail: "admin@bobbinusa.example",
      module: "Products",
      action: "Applied 10% price increase to Juki line",
      status: "pending",
      metadata: { brandLine: "Juki", percent: 10 },
      createdAt: new Date().toISOString(),
    },
    {
      _id: "preview-2",
      actorEmail: "fulfillment@bobbinusa.example",
      module: "Orders",
      action: "Marked order BOB-10479 shipped",
      status: "success",
      metadata: { carrier: "UPS" },
      createdAt: new Date().toISOString(),
    },
  ],
  modules: ["Products", "Orders", "Shipping", "Settings", "Channel Sync"],
  metrics: { eventsToday: 2, priceChanges: 1, systemJobs: 1, status: { success: 1, pending: 1 } },
  total: 2,
};

const emptyEvent = {
  module: "Admin Review",
  action: "",
  status: "success",
};

function statusTone(status) {
  if (status === "success") return "good";
  if (status === "failed") return "bad";
  return "warn";
}

function statusClasses(status) {
  if (status === "success") return "border-emerald-100 bg-emerald-50 text-emerald-700";
  if (status === "failed") return "border-red-100 bg-red-50 text-red-700";
  return "border-amber-100 bg-amber-50 text-amber-700";
}

function formatDateTime(date) {
  if (!date) return "Not recorded";

  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "America/New_York",
  }).format(new Date(date));
}

function ageLabel(date) {
  const diffMs = Date.now() - new Date(date || Date.now()).getTime();
  const minutes = Math.max(0, Math.floor(diffMs / 60000));
  if (minutes < 60) return `${minutes || 1}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function csvEscape(value) {
  const text = typeof value === "object" ? JSON.stringify(value || {}) : String(value ?? "");
  return `"${text.replaceAll('"', '""')}"`;
}

function metadataPreview(metadata) {
  const entries = Object.entries(metadata || {});
  if (!entries.length) return "No metadata";

  return entries
    .slice(0, 2)
    .map(([key, value]) => `${key}: ${typeof value === "object" ? JSON.stringify(value) : value}`)
    .join(" | ");
}

function MetricCard({ icon: Icon, label, value, note, tone = "blue" }) {
  const tones = {
    blue: "bg-blue-50 text-[#4391d1]",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    slate: "bg-slate-100 text-slate-700",
  };

  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold text-gray-500">{label}</p>
          <p className="mt-3 text-3xl font-black text-gray-900">{value}</p>
        </div>
        <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tones[tone] || tones.blue}`}>
          <Icon size={20} />
        </span>
      </div>
      <p className="mt-3 text-xs font-semibold text-gray-400">{note}</p>
    </div>
  );
}

function LoadingRows() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4].map((item) => (
        <div className="animate-pulse rounded-2xl border border-gray-100 bg-white p-4" key={item}>
          <div className="flex gap-4">
            <div className="h-11 w-11 rounded-2xl bg-gray-100" />
            <div className="flex-1 space-y-3">
              <div className="h-4 w-2/3 rounded bg-gray-100" />
              <div className="h-3 w-1/3 rounded bg-gray-100" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DetailModal({ log, onClose }) {
  if (!log) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
          <div>
            <p className="text-xs font-black uppercase text-[#4391d1]">Activity details</p>
            <h2 className="mt-2 text-xl font-black text-gray-900">{log.action}</h2>
            <p className="mt-1 text-sm font-semibold text-gray-500">{formatDateTime(log.createdAt)} ET</p>
          </div>
          <button
            className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-[#4391d1] hover:text-[#4391d1]"
            onClick={onClose}
            type="button"
          >
            <X size={18} />
          </button>
        </div>

        <div className="grid gap-4 overflow-y-auto px-6 py-5 sm:grid-cols-2">
          <DetailItem label="Module" value={log.module || "General"} />
          <DetailItem label="Status" value={log.status || "success"} />
          <DetailItem label="User" value={log.actor?.email || log.actorEmail || "System"} />
          <DetailItem label="Event ID" value={String(log._id || "").slice(-10)} />
          <div className="sm:col-span-2">
            <p className="text-xs font-black uppercase text-gray-400">Metadata</p>
            <pre className="mt-2 max-h-72 overflow-auto rounded-2xl bg-slate-950 p-4 text-xs font-semibold leading-6 text-slate-100">
              {JSON.stringify(log.metadata || {}, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div className="rounded-2xl bg-gray-50 p-4">
      <p className="text-xs font-black uppercase text-gray-400">{label}</p>
      <p className="mt-2 break-words text-sm font-black text-gray-800">{value}</p>
    </div>
  );
}

export default function ActivityLogs() {
  const [moduleFilter, setModuleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [eventForm, setEventForm] = useState(emptyEvent);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const { dismiss, toast, toasts } = useToast();

  const loadLogs = useCallback(async () => {
    const params = new URLSearchParams({
      module: moduleFilter,
      status: statusFilter,
      search,
      limit: "200",
    });
    return api.get(`/activity-logs?${params.toString()}`);
  }, [moduleFilter, search, statusFilter]);

  const { data, error, loading, reload, setData } = useAdminResource(loadLogs, fallbackPayload);
  const logs = data.logs || fallbackPayload.logs;
  const modules = data.modules || fallbackPayload.modules;
  const metrics = data.metrics || fallbackPayload.metrics;
  const total = data.total ?? logs.length;

  useEffect(() => {
    if (error && !loading) toast(`Using preview activity logs: ${error}`, "warning");
  }, [error, loading, toast]);

  const moduleCounts = useMemo(() => {
    return logs.reduce((acc, log) => {
      const key = log.module || "General";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [logs]);

  const failureCount = Number(metrics.status?.failed || 0);
  const pendingCount = Number(metrics.status?.pending || 0);
  const successCount = Number(metrics.status?.success || 0);
  const logPagination = usePagination(logs, [moduleFilter, statusFilter, search], 10);

  const updateEventForm = (event) => {
    setEventForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const createEvent = async (event) => {
    event.preventDefault();
    try {
      const data = await api.post("/activity-logs", eventForm);
      setData((current) => ({ ...current, logs: [data.activity, ...(current.logs || [])] }));
      setEventForm(emptyEvent);
      setCreateOpen(false);
      toast("Audit note recorded", "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams({ module: moduleFilter, status: statusFilter, search });
      const data = await api.get(`/activity-logs/export?${params.toString()}`);
      const rows = [
        ["Event", "User", "Module", "Status", "Time", "Metadata"],
        ...data.rows.map((row) => [row.event, row.user, row.module, row.status, row.time, row.metadata]),
      ];
      const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "activity-audit-log.csv";
      link.click();
      URL.revokeObjectURL(url);
      toast("Audit log exported", "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  return (
    <div className="space-y-6">
      <ToastStack dismiss={dismiss} toasts={toasts} />
      <DetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />

      <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-black uppercase text-[#4391d1]">
              <ShieldCheck size={14} />
              Activity Logs
            </div>
            <h1 className="mt-3 text-3xl font-black text-gray-900">Admin activity audit trail</h1>
            <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-gray-500">
              Review product edits, orders, shipping labels, settings changes, channel sync events, automation runs, and system actions in one place.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]"
              onClick={reload}
              type="button"
            >
              <RefreshCw size={15} />
              Refresh
            </button>
            <button
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]"
              onClick={exportCsv}
              type="button"
            >
              <Download size={15} />
              Export CSV
            </button>
            <button
              className="inline-flex items-center gap-2 rounded-full bg-[#4391d1] px-4 py-2 text-sm font-black text-white hover:bg-blue-600"
              onClick={() => setCreateOpen((open) => !open)}
              type="button"
            >
              <FileText size={15} />
              Add Note
            </button>
          </div>
        </div>
      </section>

      {createOpen && (
        <form className="rounded-3xl border border-blue-100 bg-white p-5 shadow-sm" onSubmit={createEvent}>
          <div className="grid gap-3 lg:grid-cols-[0.8fr_1.4fr_0.7fr_auto]">
            <label className="text-sm font-black text-gray-700">
              Module
              <input
                className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                name="module"
                onChange={updateEventForm}
                placeholder="Admin Review"
                required
                value={eventForm.module}
              />
            </label>
            <label className="text-sm font-black text-gray-700">
              Note
              <input
                className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                name="action"
                onChange={updateEventForm}
                placeholder="What should be recorded?"
                required
                value={eventForm.action}
              />
            </label>
            <label className="text-sm font-black text-gray-700">
              Status
              <select
                className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-semibold outline-none focus:border-[#4391d1]"
                name="status"
                onChange={updateEventForm}
                value={eventForm.status}
              >
                <option value="success">Success</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </label>
            <button className="self-end rounded-2xl bg-[#4391d1] px-5 py-3 text-sm font-black text-white hover:bg-blue-600" type="submit">
              Save
            </button>
          </div>
        </form>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard icon={ClipboardList} label="Events Today" note="Actions recorded since midnight" value={metrics.eventsToday || 0} />
        <MetricCard icon={CheckCircle2} label="Successful" note="Completed events in retention window" tone="emerald" value={successCount} />
        <MetricCard icon={Clock3} label="Pending" note="Items still waiting on a final state" tone="amber" value={pendingCount} />
        <MetricCard icon={AlertTriangle} label="Failures" note="Events that need admin review" tone="red" value={failureCount} />
      </div>

      <section className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-sm font-black text-gray-800">
          <Filter size={17} className="text-[#4391d1]" />
          Filters
        </div>
        <div className="grid gap-3 xl:grid-cols-[1fr_220px_180px_auto]">
          <div className="relative">
            <input
              className="w-full rounded-2xl border border-gray-200 px-4 py-3 pl-11 text-sm font-semibold outline-none focus:border-[#4391d1]"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search event, user, module"
              value={search}
            />
            <Search className="absolute left-4 top-3.5 text-gray-400" size={16} />
          </div>
          <select
            className="rounded-2xl border border-gray-200 px-4 py-3 text-sm font-bold text-gray-700 outline-none focus:border-[#4391d1]"
            onChange={(event) => setModuleFilter(event.target.value)}
            value={moduleFilter}
          >
            <option value="all">All Modules</option>
            {modules.map((moduleName) => (
              <option key={moduleName} value={moduleName}>
                {moduleName}
              </option>
            ))}
          </select>
          <select
            className="rounded-2xl border border-gray-200 px-4 py-3 text-sm font-bold text-gray-700 outline-none focus:border-[#4391d1]"
            onChange={(event) => setStatusFilter(event.target.value)}
            value={statusFilter}
          >
            <option value="all">All Status</option>
            <option value="success">Success</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>
          <button
            className="rounded-2xl border border-gray-200 px-4 py-3 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]"
            onClick={() => {
              setSearch("");
              setModuleFilter("all");
              setStatusFilter("all");
            }}
            type="button"
          >
            Clear
          </button>
        </div>
      </section>

      <div className="grid gap-6 2xl:grid-cols-[minmax(0,1fr)_360px]">
        <section className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-gray-100 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-[#4391d1]">
                <History size={20} />
              </span>
              <div>
                <h2 className="text-xl font-black text-gray-900">Recent activity</h2>
                <p className="text-sm font-semibold text-gray-400">{total} matching records, retained for 30 days</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50/60 p-4">
            {loading ? (
              <LoadingRows />
            ) : logPagination.paginatedItems.length ? (
              <div className="space-y-3">
                {logPagination.paginatedItems.map((log) => (
                  <article className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm" key={log._id}>
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="flex min-w-0 gap-4">
                        <span className={`mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border ${statusClasses(log.status)}`}>
                          {log.status === "failed" ? <AlertTriangle size={19} /> : log.status === "pending" ? <Clock3 size={19} /> : <CheckCircle2 size={19} />}
                        </span>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <StatusPill tone={statusTone(log.status)}>{log.status || "success"}</StatusPill>
                            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-black uppercase text-gray-500">{log.module || "General"}</span>
                          </div>
                          <h3 className="mt-2 text-base font-black leading-6 text-gray-900">{log.action}</h3>
                          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs font-bold text-gray-500">
                            <span className="inline-flex items-center gap-1">
                              <UserRoundCheck size={14} className="text-[#4391d1]" />
                              {log.actor?.email || log.actorEmail || "System"}
                            </span>
                            <span>{formatDateTime(log.createdAt)} ET</span>
                            <span>{ageLabel(log.createdAt)}</span>
                          </div>
                          <p className="mt-3 max-w-3xl truncate rounded-xl bg-gray-50 px-3 py-2 text-xs font-bold text-gray-500">
                            {metadataPreview(log.metadata)}
                          </p>
                        </div>
                      </div>

                      <button
                        className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-xs font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]"
                        onClick={() => setSelectedLog(log)}
                        type="button"
                      >
                        <Eye size={15} />
                        Details
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center">
                <ServerCog className="mx-auto text-gray-300" size={34} />
                <h3 className="mt-3 text-lg font-black text-gray-900">No activity found</h3>
                <p className="mt-1 text-sm font-semibold text-gray-500">Try clearing filters or refreshing the audit log.</p>
              </div>
            )}
          </div>

          <PaginationControls pagination={logPagination} />
        </section>

        <aside className="space-y-6">
          <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-[#4391d1]">
                <ServerCog size={18} />
              </span>
              <div>
                <h2 className="text-lg font-black text-gray-900">Module summary</h2>
                <p className="text-xs font-semibold text-gray-400">Current filtered results</p>
              </div>
            </div>
            <div className="mt-5 grid gap-3">
              {Object.entries(moduleCounts).length ? (
                Object.entries(moduleCounts).map(([moduleName, count]) => (
                  <button
                    className="flex items-center justify-between rounded-2xl bg-gray-50 px-4 py-3 text-left hover:bg-blue-50"
                    key={moduleName}
                    onClick={() => setModuleFilter(moduleName)}
                    type="button"
                  >
                    <span className="text-sm font-black text-gray-700">{moduleName}</span>
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-gray-500">{count}</span>
                  </button>
                ))
              ) : (
                <p className="rounded-2xl bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-500">No modules to summarize.</p>
              )}
            </div>
          </section>

          <section className="rounded-3xl bg-slate-950 p-6 text-white shadow-sm">
            <div className="flex items-center gap-3">
              <ShieldCheck className="text-sky-300" />
              <h2 className="text-lg font-black">Retention policy</h2>
            </div>
            <div className="mt-4 space-y-3 text-sm font-semibold leading-6 text-white/75">
              <p>Activity records are automatically kept for 30 days.</p>
              <p>Export filtered records before they expire when a longer audit trail is needed.</p>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
